import {
  IAircraftSwapResult,
  IFlight,
  IGroundEvent,
  IWebsocketUpdatedEvent,
  IWebsocketUpdatedEvents,
  unassignedAircraftId,
  WebsocketMessageType,
} from "@vmosolutions/oc-interfaces";
import { AnyAction } from "redux";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { IRootState } from "../../reducers/CombinedReducers";
import {
  IAircraftFlights,
  isFlight,
  isGroundEvent,
  ITimelineEvents,
  TimelineEvent,
} from "../../reducers/FlightTimelineReducer";
import { Type } from "./FlightTimeline.Actions";

/**
 * removeOldEventFromAircraft Removes the event from the aircraft
 * @param oldAircraftEvents -
 * @param actionType -
 * @param lastProcessedTime -
 * @returns ITimelineEvents
 */
const removeOldEventFromAircraft: any = (
  oldEvent: TimelineEvent,
  oldAircraftEvents: ITimelineEvents,
  lastProcessedTime: number
) => {
  const isFlightEvent = isFlight(oldEvent);
  const isGroundEvent = !isFlightEvent;

  const flights = isFlightEvent
    ? oldAircraftEvents.flights.filter((flight: IFlight) => {
        return flight._id !== oldEvent._id;
      })
    : oldAircraftEvents.flights;
  const groundEvents = isGroundEvent
    ? oldAircraftEvents.groundEvents.filter((groundEvent: IGroundEvent) => {
        return groundEvent._id !== oldEvent._id;
      })
    : oldAircraftEvents.groundEvents;

  return {
    flights,
    groundEvents,
    lastProcessedTime,
  };
};

/**
 * checkForDirtyEditorWindows Checks if any of theupdated events have open editors
 * @param flightsInEdit -
 * @param oldEvents -
 * @returns dirtyEditWindowKeys - an array of the window ids that are open and their events have been updated
 */

export const checkForDirtyEditorWindows = (flightsInEdit: any, oldEvents: (IFlight | IGroundEvent)[]): string[] => {
  const dirtyEditWindowKeys: string[] = [];
  if (flightsInEdit) {
    const keys = Object.keys(flightsInEdit);
    keys.forEach((key) => {
      const found = oldEvents.find((event) => event._id === flightsInEdit[key].flight?._id);
      if (found) {
        dirtyEditWindowKeys.push(key);
      }
    });
  }
  return dirtyEditWindowKeys;
};

const addNewEventToAircraft = (
  newEvent: TimelineEvent,
  newAircraftEvents: ITimelineEvents,
  actionType: WebsocketMessageType,
  lastProcessedTime: number
): any => {
  const newFlights = [...newAircraftEvents.flights];
  const newGroundEvents = [...newAircraftEvents.groundEvents];
  if (actionType === WebsocketMessageType.BasicFlightUpdate && isFlight(newEvent)) {
    const flightIndex = newFlights.findIndex((flight: IFlight) => flight._id.toString() === newEvent._id.toString());
    if (flightIndex >= 0) {
      newFlights[flightIndex] = newEvent;
    } else {
      newFlights.push(newEvent);
    }

    newFlights.sort((a: IFlight, b: IFlight) => {
      return new Date(a.actualOff).getTime() - new Date(b.actualOff).getTime();
    });
  } else if (isGroundEvent(newEvent)) {
    const groundEventIndex = newGroundEvents.findIndex(
      (groundEvent: IGroundEvent) => groundEvent._id.toString() === newEvent._id.toString()
    );
    if (groundEventIndex >= 0) {
      newGroundEvents[groundEventIndex] = newEvent;
    } else {
      newGroundEvents.push(newEvent);
    }

    newGroundEvents.sort((a: IGroundEvent, b: IGroundEvent) => {
      return new Date(a.actualStart).getTime() - new Date(b.actualStart).getTime();
    });
  }

  return {
    flights: [...newFlights],
    groundEvents: [...newGroundEvents],
    lastProcessedTime,
  };
};

const getEmptyTimelineEvents = (lastProcessedTime: number): ITimelineEvents => {
  return {
    flights: [],
    groundEvents: [],
    lastProcessedTime,
  };
};

export const webSocketSwapAircraftEventsUpdated = (
  aircraftSwapResult: IAircraftSwapResult
): ThunkAction<void, IRootState, object, AnyAction> => {
  return (dispatch: ThunkDispatch<IRootState, object, AnyAction>, getState: () => IRootState): void => {
    const aircraftEvents = { ...getState().timelineFlights.flightData };
    const lastProcessedTime = Date.now();
    aircraftEvents[aircraftSwapResult.firstAircraft].lastProcessedTime = lastProcessedTime;
    aircraftEvents[aircraftSwapResult.secondAircraft].lastProcessedTime = lastProcessedTime;
    const { firstAircraft, secondAircraft } = aircraftSwapResult;
    const eventData: IAircraftFlights = {
      [firstAircraft]: {
        flights: [...aircraftEvents[firstAircraft].flights],
        groundEvents: [...aircraftEvents[firstAircraft].groundEvents],
        lastProcessedTime,
      },
      [secondAircraft]: {
        flights: [...aircraftEvents[secondAircraft].flights],
        groundEvents: [...aircraftEvents[secondAircraft].groundEvents],
        lastProcessedTime,
      },
    };

    aircraftSwapResult.updatedFirstAircraftEvents.forEach((event: IFlight | IGroundEvent) => {
      const eventProp = isFlight(event) ? "flights" : "groundEvents";
      const eventIndex = aircraftEvents[aircraftSwapResult.firstAircraft][eventProp].findIndex(
        (value: IFlight | IGroundEvent) => {
          return value._id === event._id;
        }
      );
      if (eventIndex >= 0) {
        eventData[firstAircraft][eventProp].splice(eventIndex, 1);
      }

      if (isFlight(event)) {
        eventData[secondAircraft].flights.push(event);
      } else {
        eventData[secondAircraft].groundEvents.push(event);
      }
    });

    aircraftSwapResult.updatedSecondAircraftEvents.forEach((event: IFlight | IGroundEvent) => {
      const eventProp = isFlight(event) ? "flights" : "groundEvents";
      const eventIndex = aircraftEvents[aircraftSwapResult.secondAircraft][eventProp].findIndex(
        (value: IFlight | IGroundEvent) => {
          return value._id === event._id;
        }
      );
      if (eventIndex >= 0) {
        aircraftEvents[aircraftSwapResult.secondAircraft][eventProp].splice(eventIndex, 1);
      }
      if (isFlight(event)) {
        aircraftEvents[aircraftSwapResult.firstAircraft].flights.push(event);
      } else {
        aircraftEvents[aircraftSwapResult.firstAircraft].groundEvents.push(event);
      }
    });

    aircraftEvents[aircraftSwapResult.firstAircraft].flights.sort((a: IFlight, b: IFlight) => {
      return new Date(a.actualOff).getTime() - new Date(b.actualOff).getTime();
    });
    aircraftEvents[aircraftSwapResult.firstAircraft].groundEvents.sort((a: IGroundEvent, b: IGroundEvent) => {
      return new Date(a.actualStart).getTime() - new Date(b.actualStart).getTime();
    });
    aircraftEvents[aircraftSwapResult.secondAircraft].flights.sort((a: IFlight, b: IFlight) => {
      return new Date(a.actualOff).getTime() - new Date(b.actualOff).getTime();
    });
    aircraftEvents[aircraftSwapResult.secondAircraft].groundEvents.sort((a: IGroundEvent, b: IGroundEvent) => {
      return new Date(a.actualStart).getTime() - new Date(b.actualStart).getTime();
    });

    dispatch({
      type: Type.TIMELINE_GOT_UPDATED_FLIGHT,
      flightData: { ...eventData },
    });
  };
};

export const websocketUpdatedFlightEvents = (
  updatedEvent: IWebsocketUpdatedEvents,
  actionType: WebsocketMessageType
): ThunkAction<void, IRootState, object, AnyAction> => {
  return (dispatch: ThunkDispatch<IRootState, object, AnyAction>, getState: () => IRootState): void => {
    if (!updatedEvent) {
      return;
    }

    const { newEvents, oldEvents } = updatedEvent;
    const aircraftEvents: IAircraftFlights = {
      ...getState().timelineFlights.flightData,
    };

    const eventData: IAircraftFlights = {};
    const lastProcessedTime = Date.now();
    oldEvents.forEach((oldEvent: TimelineEvent) => {
      updateOldEvent(eventData, aircraftEvents, oldEvent, lastProcessedTime);
    });
    newEvents.forEach((newEvent: TimelineEvent) => {
      updateNewEvent(eventData, aircraftEvents, actionType, newEvent, lastProcessedTime);
    });

    dispatch({
      type: Type.TIMELINE_GOT_UPDATED_FLIGHT,
      flightData: { ...eventData },
      oldEvents: oldEvents,
    });

    // If and only if the item being updated is selected, update it in selectedEvents as well.
    const selectedEvents = getState().timelineFlights.selectedEvents;
    if (!!selectedEvents?.length && !!newEvents?.length) {
      // Check if there is any overlap between selectedEvents and newEvents
      const newEventsSet = new Set(newEvents.map((ne) => ne?._id));
      if (selectedEvents.some((se) => newEventsSet.has(se?._id))) {
        const updatedSelectedEvents = selectedEvents?.map((e) => {
          const updatedEvent = newEvents.find((ne) => ne?._id === e?._id);
          return updatedEvent?._id ? updatedEvent : e;
        });
        dispatch({
          type: Type.TIMELINE_SET_SELECTED_EVENTS,
          selectedEvents: updatedSelectedEvents,
        });
      }
    }

    //check if any of the updated events have open editors
    const flightsInEdit = getState().timelineFlights.editFlights;
    const dirtyEditFlightWindowKeys = checkForDirtyEditorWindows(flightsInEdit, oldEvents);
    dirtyEditFlightWindowKeys?.forEach((key: string) => {
      dispatch({ type: Type.TIMELINE_ISDIRTY_EDIT_FLIGHT, windowId: key, isDirty: true });
    });
  };
};

export const webSocketUpdatedEvent = (
  updatedEvent: IWebsocketUpdatedEvent,
  actionType: WebsocketMessageType
): ThunkAction<void, IRootState, object, AnyAction> => {
  return (dispatch: ThunkDispatch<IRootState, object, AnyAction>, getState: () => IRootState): void => {
    if (!updatedEvent) {
      return;
    }

    const { newEvent, oldEvent } = updatedEvent;
    const aircraftEvents: IAircraftFlights = {
      ...getState().timelineFlights.flightData,
    };

    const eventData: IAircraftFlights = {};
    const lastProcessedTime = Date.now();
    updateOldEvent(eventData, aircraftEvents, oldEvent, lastProcessedTime);
    updateNewEvent(eventData, aircraftEvents, actionType, newEvent, lastProcessedTime);
    dispatch({
      type: Type.TIMELINE_GOT_UPDATED_FLIGHT,
      flightData: { ...eventData },
      oldEvents: [oldEvent],
    });

    // If item being updated is selected, update it in selectedEvents as well.
    const selectedEvents = getState().timelineFlights.selectedEvents;
    const selectedEventsSet = new Set(selectedEvents.map((se) => se?._id));
    if (selectedEventsSet.has(newEvent?._id)) {
      const updatedSelectedEvents = selectedEvents?.map((e) => (e?._id === newEvent?._id ? newEvent : e));
      dispatch({
        type: Type.TIMELINE_SET_SELECTED_EVENTS,
        selectedEvents: updatedSelectedEvents,
      });
    }

    //check if any of the updated events have open editors
    const flightsInEdit = getState().timelineFlights.editFlights;
    const dirtyEditFlightWindowKeys = checkForDirtyEditorWindows(flightsInEdit, [oldEvent]);
    dirtyEditFlightWindowKeys?.forEach((key: string) => {
      dispatch({ type: Type.TIMELINE_ISDIRTY_EDIT_FLIGHT, windowId: key, isDirty: true });
    });
  };
};

const updateOldEvent = (
  eventData: IAircraftFlights,
  aircraftEvents: IAircraftFlights,
  oldEvent: IGroundEvent | IFlight,
  lastProcessedTime: number
): void => {
  if (oldEvent) {
    const oldAircraft = oldEvent.aircraft ? oldEvent.aircraft._id : unassignedAircraftId;
    const oldAircraftEvents = aircraftEvents[oldAircraft];
    if (oldAircraftEvents) {
      const removedEvents = removeOldEventFromAircraft(oldEvent, oldAircraftEvents, lastProcessedTime);
      eventData[oldAircraft] = removedEvents;
      aircraftEvents[oldAircraft] = removedEvents;
    }
  }
};

const updateNewEvent = (
  eventData: IAircraftFlights,
  aircraftEvents: IAircraftFlights,
  actionType: WebsocketMessageType,
  newEvent: IGroundEvent | IFlight,
  lastProcessedTime: number
): void => {
  if (newEvent) {
    const newAircraft = newEvent.aircraft ? newEvent.aircraft._id : unassignedAircraftId;

    if (!eventData[newAircraft]) {
      eventData[newAircraft] = getEmptyTimelineEvents(lastProcessedTime);
    }
    if (!aircraftEvents[newAircraft]) {
      aircraftEvents[newAircraft] = getEmptyTimelineEvents(lastProcessedTime);
    }
    const newAircraftEvents = aircraftEvents[newAircraft];
    const test = addNewEventToAircraft(newEvent, newAircraftEvents, actionType, lastProcessedTime);
    eventData[newAircraft] = test;
    aircraftEvents[newAircraft] = test;
  }
};

// export const refreshAircraftFlights = (
//   aircraftsToRefresh: string[]
// ): ThunkAction<Promise<void>, IRootState, {}, AnyAction> => {
//   return async (dispatch: ThunkDispatch<IRootState, {}, AnyAction>, getState: () => IRootState): Promise<void> => {
//     const updatedAircaftEvents: IAircraftFlights = {};
//     const lastProcessedTime = Date.now();

//     const userSession = await Auth.currentSession();
//     const token = userSession.getIdToken().getJwtToken();

//     const currentDate = new Date();
//     const startDate = new Date(new Date().setDate(currentDate.getUTCDate() - 45));
//     const endDate = new Date(new Date().setDate(currentDate.getUTCDate() + 45));
//     for (const aircraftId of aircraftsToRefresh) {
//       if (aircraftId === unassignedAircraftId) {
//         dispatch(getUnassignedAircraftFlights());
//       } else {
//         const aircraftEvents = await API.get(
//           "skytrains",
//           `/flights/${aircraftId}/events?startTime=${startDate?.valueOf()}&endTime=${endDate?.valueOf()}`,
//           {
//             headers: {
//               Accept: "*/*",
//               Authorization: token,
//             },
//           }
//         );
//         if (!aircraftEvents.flights || !aircraftEvents.groundEvents) {
//           return;
//         }

//         updatedAircaftEvents[aircraftId] = {
//           ...aircraftEvents,
//           lastProcessedTime,
//         };
//       }
//     }
//     dispatch({
//       type: Type.TIMELINE_GOT_UPDATED_FLIGHT,
//       flightData: { ...updatedAircaftEvents },
//     });
//   };
// };
